import { api } from "./api.js";
import type { ChecklistItem, ChecklistProgress } from "./taskChecklist.js";

// ── Types ──────────────────────────────────────────────────────────────────
export type ProjectStatus = "not_started" | "in_progress" | "on_hold" | "completed" | "cancelled";
export type BillingType = "fixed" | "hourly" | "none";
export type TaskStatus = "todo" | "in_progress" | "review" | "done";
export type TaskPriority = "low" | "normal" | "high" | "urgent";
export type ContractStatus = "draft" | "sent" | "active" | "expired" | "terminated";

export interface ClientRef { id: string; name: string; kind: string }
export interface ProjectRef { id: string; name: string; clientId: string }

export interface Project {
  id: string;
  clientId: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  billingType: BillingType | null;
  budgetCents: number | null;
  hourlyRateCents: number | null;
  /**
   * The hours the project was SOLD on, so an overview can put planned beside
   * actual instead of showing logged time against nothing.
   *
   * Nullable and never defaulted to 0, which is the point of the column: most
   * rows genuinely never had an estimate because the old system never asked for
   * one, and a zero would read as "we estimated this at zero hours" - the one
   * reading of the two that makes every logged hour look like an overrun. A
   * screen has to say "no estimate" rather than draw a bar against a number
   * nobody chose.
   *
   * A float, not an int (`Float?` on the server), so half an hour is
   * representable. `Task.estimateHours` is NOT summed into it: the two answer
   * different questions - what was promised up front versus how the team broke
   * the work down afterwards - and are allowed to disagree.
   *
   * Written by the project form and cleared by emptying its box: `createBody`
   * and `patchBody` both take it, and `patchBody` takes it as NULLABLE - the one
   * figure on this record a PATCH can actually remove. `budgetCents` and
   * `hourlyRateCents` cannot, which is why `projectPatch` drops an emptied one
   * of those and warns instead.
   *
   * It comes back on every project read: the list and the detail both use
   * `include`, so every scalar travels. The SUMMARY endpoint reads a narrow
   * select and does not carry it, so a screen putting planned beside actual
   * takes the estimate from the project row and the logged minutes from the
   * summary.
   */
  estimatedHours: number | null;
  startDate: string | null;
  deadline: string | null;
  completedAt: string | null;
  /**
   * When the project left the daily register - the exit that is NOT deletion.
   *
   * Not optional and never absent: every project read on this side comes back
   * through an `include`, so the column always travels, and a `?` here would let
   * a screen confuse "this project is live" with "nobody asked". A non-null
   * value is the whole state - there is no separate boolean to disagree with it.
   *
   * A row carrying a date is one an operator has taken out of the lists and out
   * of the pickers for new work. Nothing that BELONGS to it is hidden: its
   * hours, invoices, milestones and expenses stay in their own registers exactly
   * as they were. That asymmetry is stated in full on the server
   * (`crm-projects/archive.routes.ts`) and promised to the operator in
   * `projects.archivedHint`; a screen must not quietly widen it.
   *
   * The register hides these by default - the endpoint's `archived` parameter
   * defaults to "hide" - so on the ordinary list this is null on every row. It
   * is still worth drawing wherever a project can be reached another way: the
   * detail page resolves an archived project by id on purpose, and a record that
   * is hidden from the list but looks identical when you open it by link is a
   * record whose state is invisible exactly when it matters.
   */
  archivedAt: string | null;
  progressPct: number;
  /**
   * Who owns the number above. "manual" means an operator typed `progressPct`
   * and it stays where they left it; "tasks" means the stored value is dead
   * weight and the real figure is recomputed from the task rollup on read.
   *
   * Not optional, for the same reason as the delivery block: the list and the
   * detail both read with `include`, so the column always comes back, and `?`
   * would let a screen confuse "the answer is manual" with "nobody asked" - the
   * row would quietly render as manual either way.
   *
   * The project row keeps carrying the STORED `progressPct` under both settings.
   * A screen that wants the effective percentage for a "tasks" project reads the
   * summary, which is the endpoint that does the recomputation.
   */
  progressSource: "manual" | "tasks";
  /**
   * The percentage a screen should PRINT, already resolved against
   * `progressSource`: the stored figure on a manual project, the task rollup on a
   * "tasks" one. The two fields exist side by side because they answer different
   * questions - `progressPct` is what sits in the column (what the toggle gives
   * back when the source returns to "manual"), this is what is true right now. On
   * a manual project they are equal, which is the visible proof that nothing is
   * being overridden.
   *
   * Optional because ONLY the list endpoint computes it. The detail and the PATCH
   * response carry the row as it is stored, with no task tally attached. That does
   * NOT mean a moved roadmap card loses the field: the board writes a change back
   * by merging the response onto the row it already holds, and a response with no
   * such key cannot overwrite one. The optionality is therefore about which
   * endpoint a row came from, not about a value that goes missing mid-session.
   * A screen without a value here must still fall back to `progressPct` rather
   * than render nothing: on a "tasks" project that is a stale number, and a stale
   * number beats a blank bar, which reads as a failed load.
   * The project page itself needs neither - it prints the summary's `progressPct`,
   * which the server resolves the same way.
   */
  effectiveProgressPct?: number;
  members: string[];
  tags: string[];
  /**
   * The delivery record: what was actually built here.
   *
   * Not optional, even though every one of them is empty on a project typed in
   * today. The list endpoint and the detail endpoint both read with `include`,
   * so every scalar column comes back on both, and marking these `?` would let
   * a screen forget to handle the answer rather than the absence. The three
   * lists are `string[]` and never null: a Prisma scalar list has no null, only
   * empty, which is why the route accepts them as optional but not nullable.
   *
   * `servicesRaw` is the untouched Perfex text beside the normalised
   * `services`. It is never shown as a list of its own - see `DeliveryCard`,
   * which uses it only to surface the values the current vocabulary lost.
   */
  platform: string | null;
  logoStatus: string | null;
  services: string[];
  servicesRaw: string[];
  features: string[];
  /** Free text, not a number: the old system recorded a range ("1-5", "10+"). */
  pages: string | null;
  /**
   * Operational links: an operator's bookmarks into other people's panels.
   * Free text validated by nothing, so a value here can be a full URL, a bare
   * hostname or a sentence. Turning one into an `href` is `panelHref`'s job in
   * `DeliveryCard.tsx`, and it refuses more than it accepts.
   */
  adminUrl: string | null;
  hostingUrl: string | null;
  dbAdminUrl: string | null;
  cpanelUrl: string | null;
  templateUrl: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Whether THIS operator has this project on their shortlist.
   *
   * Optional for the same reason `effectiveProgressPct` above is: it is a
   * statement about which endpoint the row came from. The register and the
   * detail read attach it; the archive routes, the copy route and the PATCH
   * answer without it, because none of them touches a pin and a field they
   * invented would be a guess.
   *
   * A screen without a value must read it as "not pinned" rather than as "do not
   * draw the control" - the menu still has to offer Pin, or a project reached by
   * a route that omits the field becomes unpinnable.
   *
   * It is per-operator (`ProjectPin.ownerId`), so two people legitimately get
   * different answers for the same project. Nothing may cache it across users.
   */
  pinned?: boolean;
  client?: ClientRef;
  milestones?: Milestone[];
  _count?: { tasks: number; timeEntries?: number };
}

export interface Milestone {
  id: string;
  projectId: string;
  name: string;
  /**
   * When the milestone is meant to BEGIN, beside `dueDate`'s end. Null on every
   * imported row and on every milestone typed before this field existed: the old
   * system recorded a deadline and nothing else. A timeline that wants to draw a
   * bar therefore has to survive a start it does not have, rather than assume
   * one - a missing start is not "starts today".
   */
  startDate: string | null;
  dueDate: string | null;
  done: boolean;
  sortOrder: number;
}

/**
 * One invoice as the project summary reports it.
 *
 * Named rather than written inline because the tab that draws these declared a
 * narrower copy of its own and threw three of the fields away: the server has
 * always selected the payment, the currency and the issue date, and the screen
 * showed number, status and total. These seven are exactly what
 * `crm-projects/projects.routes.ts` selects, so widening one has to widen both.
 */
export interface ProjectSummaryInvoice {
  id: string;
  number: string;
  status: string;
  totalCents: number;
  paidCents: number;
  currency: string;
  issueDate: string;
}

/** One estimate as the project summary reports it. Five fields, not seven: an
 *  estimate takes no payments and carries no issue date of its own. */
export interface ProjectSummaryEstimate {
  id: string;
  number: string;
  status: string;
  totalCents: number;
  currency: string;
}

export interface ProjectSummary {
  budgetCents: number | null;
  hourlyRateCents: number | null;
  billingType: BillingType | null;
  totalMinutes: number;
  billableMinutes: number;
  /**
   * Three words that blur into one, so the boundary is written down here too
   * (the server states it at the arithmetic). BILLABLE is work the client can
   * be charged for; BILLED is billable work that already sits on an invoice
   * (`TimeEntry.invoicedId` is set); UNBILLED is the remainder - done,
   * chargeable, not yet asked for. A NON-billable minute belongs to neither
   * `billedMinutes` nor `unbilledMinutes`: it will never go on an invoice at
   * all, and counting it as unbilled would invent revenue.
   */
  billedMinutes: number;
  unbilledMinutes: number;
  laborCostCents: number;
  expenseCents: number;
  spentCents: number;
  invoicedCents: number;
  /** How much of the budget is used, for the bar. Null when there is no budget
   *  to draw against, or a zero one there is no percentage of. */
  budgetUsedPct: number | null;
  /**
   * Whether the project is past its budget, decided by the API in cents.
   *
   * NOT `budgetUsedPct > 100`, which is what this screen used to test. That
   * number is rounded to a whole percent, so a project 100.2% through its budget
   * came back as 100 and read as healthy - while the register header above it,
   * which compares cents, already counted the same project as over budget. One
   * rule now, and it lives in the API (`crm-projects/lib/budget.ts`) where the
   * portfolio rollup reads it too.
   */
  overBudget: boolean;
  taskCounts: { todo: number; in_progress: number; review: number; done: number; total: number };
  /** Open tasks whose own due date has passed. A task without a due date is
   *  never overdue, which is why this is not `total - done`. */
  overdueTasks: number;
  /**
   * The percentage question, answered twice on purpose.
   *
   * `progressPct` is the number a screen should PRINT. The server has already
   * applied `progressSource` to it, so it is the stored figure on a manual
   * project and the task rollup on a "tasks" one; no caller has to branch to
   * display it. `derivedProgressPct` is what the tasks say REGARDLESS of the
   * setting - always computed, and 0 when the project has no tasks at all
   * (which is why 0 here means "nothing to go on", not "no work done").
   *
   * Both travel because the toggle has to show what switching would do BEFORE
   * the operator switches. With only the effective number, "let the tasks
   * decide" is a button whose consequence you can read only after pressing it,
   * and on a project sitting at a hand-typed 80 the honest preview is the 45
   * the tasks would drop it to. On a "tasks" project the two are equal - that is
   * not redundancy, it is the visible proof that nothing is being overridden.
   */
  progressSource: "manual" | "tasks";
  derivedProgressPct: number;
  progressPct: number;
  /**
   * Task rollup per milestone, joined by id to the `milestones` on the project
   * detail rather than merged into it: the summary and the detail are two
   * fetches that land in either order, and folding the counts into `Milestone`
   * would mean a milestone rendering as 0/0 during the gap. A milestone with no
   * entry here has no tasks pointing at it, which is a different statement from
   * "the counts have not arrived".
   */
  milestones: { id: string; done: number; total: number }[];
  /**
   * The last time anything happened on this project: the newest of the project
   * row itself, its newest task and its newest time entry. It is a MAXIMUM over
   * three sources, not the project's own `updatedAt` - a project whose tasks
   * moved all week has not been idle just because nobody edited its header.
   *
   * Typed nullable although the server can hardly ever produce null (the project
   * row always has an `updatedAt`), so that a screen has to answer the absence
   * rather than print an empty date as "just now".
   */
  lastActivityAt: string | null;
  /**
   * What each linked tab has behind it, as four integers.
   *
   * Counts and not the lists, because a tab loads its own rows when it is
   * opened: the summary only has to say whether opening it is worth the click,
   * and shipping every contract, cost and ticket of a long-running project to
   * answer that would make the page slower the more history it has.
   *
   * NOT optional. `buildProjectSummary` fills all four on every response - the
   * three queries behind them run unconditionally in the same `Promise.all` as
   * the rest - so a `?` here would hand every reader an absence that can never
   * happen, and hide the real regression the day the server stops sending it.
   *
   * `openTickets` is a SUBSET of `tickets` counted from the same grouped rows:
   * open, in progress and waiting count, resolved and closed do not. Zeros are
   * the normal state today - the project link is new and little is filed under it.
   */
  linkedCounts: {
    contracts: number;
    expenses: number;
    tickets: number;
    openTickets: number;
    /** Files attached to the PROJECT itself. The rows hanging off its tasks are
     *  not added in, on the server or here: the files tab lists exactly what
     *  this number counts, and a pill that promised more than the list shows
     *  would teach an operator to distrust the counters beside it too. */
    files: number;
    /** Entries on the project's timeline: notes plus the handful of events the
     *  platform files by itself. Unlike the registers above it grows on its own,
     *  so the pill answers "is there anything recorded here" rather than "how
     *  busy has this been". */
    activity: number;
    /** Credential entries in the vault that name this project. How many, and
     *  nothing else - not which systems, not whether a password is on file. The
     *  tab that prints it is drawn only for an operator holding `read:crm.vault`
     *  (see `sections/projects/tabs.ts`), so for anybody else this number never
     *  reaches a screen. */
    vault: number;
    /** Time entries logged against the project. ROWS, not minutes - the hours are
     *  already printed in the KPI strip, and the pill answers the same question
     *  every other pill does: is there anything behind this tab. */
    timeEntries: number;
  };
  invoices: ProjectSummaryInvoice[];
  estimates: ProjectSummaryEstimate[];
}

/**
 * What deleting a project would take with it.
 *
 * Task, Milestone and TimeEntry all cascade off Project in the CRM schema, so
 * the delete is never just the one row. The confirm dialog reads these through
 * `getProjectDeletePreview` and the DELETE recounts them at the moment it
 * decides, from one definition on the server - the dialog cannot promise a
 * different destruction from the one the button performs.
 *
 * `invoicedTimeEntries` is the subset that refuses the delete outright: those
 * hours are already on an invoice, and a cascade that erased them would leave
 * nobody able to say what the client was charged for.
 */
export interface ProjectDeleteCounts {
  tasks: number;
  milestones: number;
  timeEntries: number;
  invoicedTimeEntries: number;
}

/** The ticket a task came out of, as much of it as a badge needs. */
export interface TaskTicketRef {
  id: string;
  number: string;
  subject: string;
  status: "open" | "in_progress" | "waiting" | "resolved" | "closed";
}

export interface Task {
  id: string;
  projectId: string | null;
  milestoneId: string | null;
  clientId: string | null;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  assignees: string[];
  startDate: string | null;
  dueDate: string | null;
  completedAt: string | null;
  estimateHours: number | null;
  billable: boolean;
  /**
   * The task's own labels.
   *
   * The same vocabulary `Project.tags` holds, and deliberately the same rules:
   * one ceiling pair, one normaliser, case PRESERVED and matched exactly
   * (`crm-projects/lib/tags.ts` on the API side). The filter uses array
   * containment, so the writer decides what the reader can ever find - a screen
   * that lower-cased a tag on its way in would build a second vocabulary that
   * looks identical and matches nothing. `TagChips` is the control that writes
   * both columns, which is what keeps the promise.
   */
  tags: string[];
  /**
   * Whether the customer sees this task in the client portal. The portal is the
   * only reader in the whole repo; every CRM screen lists the task either way,
   * so an operator who ticks this off is hiding it from the client and from
   * nobody else. True is the default a task created here gets, which is also
   * why a row that was hidden in the old CRM has to be written false out loud.
   */
  visibleToClient: boolean;
  /**
   * The invoice that quoted this task's recorded work, or null.
   *
   * A RECORD, not a lock. Unlike `TimeEntry.invoicedId`, nothing refuses an edit
   * or a delete because of this column: the work can still change after its hours
   * were billed, and hours logged afterwards go on the next invoice. A screen
   * must not read it as "finished" or "read-only".
   *
   * It holds ONE id while a task can appear on many invoices, and `invoice-time`
   * stamps only rows where it is still null - so the FIRST invoice keeps the
   * link, and following it lands on the earliest document, not the latest.
   */
  invoicedId: string | null;
  /**
   * The support ticket this task was made from, or null.
   *
   * A real relation on the API side, unlike `invoicedId` above it: a ticket that
   * is deleted clears this column, so an id here always names a ticket that can
   * be opened. That is the whole reason it is a foreign key - a task claiming an
   * origin nobody can reach looks exactly like one whose origin works.
   */
  ticketId: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  project?: ProjectRef | null;
  /** The ticket named by `ticketId`, on the DETAIL only. It carries `number`
   *  because a task page has to print TIC-2026-0041 rather than a cuid. */
  ticket?: TaskTicketRef | null;
  comments?: TaskComment[];
  timeEntries?: TimeEntry[];
  /** The boxes, on the DETAIL only. The list endpoint sends the rollup below
   *  instead, because a page of five hundred tasks does not want five hundred
   *  lists of text it has nowhere to draw. */
  checklist?: ChecklistItem[];
  /**
   * done/total for the boxes, on the LIST only, counted for the whole page in
   * one query. Optional because the detail does not send it - a screen holding a
   * detail has the items themselves and should count those rather than wait for
   * a figure that is not coming.
   */
  checklistProgress?: ChecklistProgress;
  _count?: { comments: number };
}

export interface TaskComment {
  id: string;
  taskId: string;
  author: string;
  body: string;
  createdAt: string;
}

export interface TimeEntry {
  id: string;
  taskId: string | null;
  projectId: string | null;
  userId: string;
  minutes: number;
  note: string | null;
  startedAt: string;
  billable: boolean;
  invoicedId: string | null;
  createdAt: string;
  task?: { id: string; title: string } | null;
}

export interface Contract {
  id: string;
  clientId: string;
  /**
   * The project this contract covers, when it covers one.
   *
   * A loose string and NOT a relation - the house pattern for a project link on
   * a sales or support document, as on `Expense.projectId` and `Ticket.projectId`.
   * The database checks nothing here, so this id can name a project that no
   * longer exists and a screen resolving a name from it has to survive finding
   * none. Null is the normal state and not a gap to fill: a retainer covering the
   * whole relationship belongs to the client and to no single project.
   */
  projectId: string | null;
  number: string | null;
  title: string;
  type: string | null;
  status: ContractStatus;
  valueCents: number | null;
  currency: string;
  startDate: string | null;
  endDate: string | null;
  signedAt: string | null;
  content: unknown;
  signature?: ContractSignature | null;
  templateKey?: string | null;
  renewReminderAt: string | null;
  /**
   * When the renewal reminder actually WENT OUT, which is a statement about the
   * past. A contract carrying a date here has already had its mail sent, so a
   * screen that reads `renewReminderAt` alone and says "reminder set" is
   * describing something that already happened, not something coming.
   *
   * Null means the reminder is still owed. It is also the only thing stopping
   * the sweep from re-sending on every tick, which is why moving
   * `renewReminderAt` to a new date clears this back to null on the server: the
   * two fields are read together or not at all.
   */
  renewNotifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
  client?: ClientRef;
}

export interface ContractSignature {
  name: string;
  email?: string;
  ip?: string;
  method?: string;
  signedAt?: string;
}

/** A template's localised field - `{ et, en }`. */
type Localized = Record<string, string>;

export interface ContractTemplate {
  id: string;
  key: string;
  name: Localized;
  description: Localized;
  type: Localized;
  defaultValueCents: number | null;
  durationMonths: number | null;
  builtin: boolean;
  sortOrder: number;
}

/** A template with its body, which is what the editor needs. */
export interface ContractTemplateFull extends ContractTemplate {
  body: Localized;
}

/** The input a template create or update takes. */
export interface ContractTemplateInput {
  key?: string;
  name: Localized;
  description: Localized;
  type: Localized;
  defaultValueCents?: number | null;
  durationMonths?: number | null;
  body: Localized;
  sortOrder?: number;
}

interface Page<T> { rows: T[]; total: number; page: number; pageSize: number; pageCount: number }

function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return "";
  const p = Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
}

// ── Projects ───────────────────────────────────────────────────────────────

/**
 * The narrowing filters the project endpoints take, and the ONE description of
 * them on this side.
 *
 * A `type` and not an `interface` on purpose: only a type alias gets TypeScript's
 * implicit index signature, which is what lets one of these be handed to
 * `getProjectPortfolio`, whose parameter is a plain `Record`. An interface here
 * compiles everywhere except at that one call site, and the error it produces
 * there ("index signature is missing") reads as a bug in the caller.
 *
 * Every field is also a field of the API's `filterShape`
 * (`crm-projects/lib/projectFilters.ts`), and the register and the portfolio
 * rollup both accept exactly this set. That is the whole reason it is named: the
 * money above the list and the rows in it must be asked the same question, and a
 * filter added to one signature and not the other is a header that quietly totals
 * a different portfolio.
 *
 * The two dates are ISO INSTANTS, not `YYYY-MM-DD` days - the endpoint parses
 * them with `z.string().datetime()` and refuses a bare day. Which instant a day
 * turns into is a decision with a timezone in it; it is made once, in
 * `crm/sections/projects/listView.ts`.
 */
export type ProjectFilterParams = {
  status?: ProjectStatus;
  clientId?: string;
  deadlineFrom?: string;
  deadlineTo?: string;
  /** One tag. `has` on the column, so it matches exactly - the writers normalise
   *  case through `lib/tags.ts` so this cannot silently miss rows. */
  tag?: string;
  /** One person's id, matched against `Project.members`. */
  member?: string;
  /** Defaults to "hide" AT THE ENDPOINT. Sending nothing is the daily register. */
  archived?: "hide" | "only" | "all";
};

/**
 * Everything GET /admin/crm/projects takes: the filters above, plus the three
 * things that are about the ANSWER rather than the set - a search term, an order
 * and a page.
 *
 * `q` is deliberately absent from `ProjectFilterParams`: the rollup refuses it,
 * because there is no honest way to print a total over "whatever matched veeb".
 */
export type ProjectListParams = ProjectFilterParams & {
  q?: string;
  sort?: "created" | "name" | "deadline" | "budget" | "progress";
  dir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
};

export const getProjects = (params?: ProjectListParams) =>
  api.get<Page<Project>>(`/admin/crm/projects${qs(params)}`);

/**
 * The tag vocabulary the register's tag filter offers - `{ rows: string[] }`,
 * folded from the projects themselves rather than kept in a table.
 *
 * ARCHIVED PROJECTS ARE NOT IN IT, and that is the server's doing (see the route
 * in `projects.routes.ts`). It matters at this end too: a filter control is a
 * promise about the list beneath it, so a tag that only archived projects carry
 * would be a choice that returns an empty list and no explanation.
 */
export const getProjectTags = () =>
  api.get<{ rows: string[] }>("/admin/crm/project-tags");
export const getProject = (id: string) =>
  api.get<Project & { milestones: Milestone[]; _count: { tasks: number; timeEntries: number } }>(`/admin/crm/projects/${id}`);
export const getProjectSummary = (id: string) =>
  api.get<ProjectSummary>(`/admin/crm/projects/${id}/summary`);
/**
 * The rollup for a whole filtered SET of projects lives in `projectPortfolio.ts`
 * and is re-exported here so both import paths work: this module is where callers
 * already look for a project call, and the sibling exists because this file had
 * reached the size where the next feature has to go somewhere else (AGENTS.md
 * §4). A sibling, not a folder move - the public path is unchanged.
 */
export { getProjectPortfolio } from "./projectPortfolio.js";
export type { ProjectPortfolio } from "./projectPortfolio.js";
export const createProject = (body: Record<string, unknown>) =>
  api.post<Project>("/admin/crm/projects", body);
export const updateProject = (id: string, body: Record<string, unknown>) =>
  api.patch<Project>(`/admin/crm/projects/${id}`, body);
/** The counts BEFORE the button, so the confirm dialog can name what it is
 *  about to destroy. Guarded by `view:crm`, not by the write right: opening the
 *  dialog is reading, pressing the button is not. */
export const getProjectDeletePreview = (id: string) =>
  api.get<ProjectDeleteCounts>(`/admin/crm/projects/${id}/delete-preview`);
/**
 * 200 carries the counts of what it destroyed rather than a bare `ok`.
 *
 * A project holding invoiced time is refused instead: `409 { error:
 * "has_invoiced_time", counts }`, which arrives here as an `ApiError` with
 * `status === 409` and those same four numbers on `body`. Nothing is deleted in
 * that case, so the screen has to say why rather than show a generic failure.
 */
export const deleteProject = (id: string) =>
  api.del<{ ok: boolean; counts: ProjectDeleteCounts }>(`/admin/crm/projects/${id}`);

/**
 * Out of the register, and back again.
 *
 * Two calls on one path rather than a flag on `updateProject`, mirroring the
 * server: the date is the server's to set, and a column the generic PATCH could
 * write is a column an over-eager form clears by sending the whole record back.
 * DELETE here removes the ARCHIVE RECORD - the date - and not the project; the
 * project's own destroyer is `deleteProject` above, on the path without the
 * `/archive` suffix.
 *
 * Both answer with the saved project, so a screen swaps the row it is holding
 * for the one that comes back instead of refetching. Both are idempotent: asking
 * for a state the row is already in returns it unchanged and writes no audit
 * line, so a double click on a slow connection cannot move the date that says
 * when the project left the list.
 */
export const archiveProject = (id: string) =>
  api.post<Project>(`/admin/crm/projects/${id}/archive`);
export const unarchiveProject = (id: string) =>
  api.del<Project>(`/admin/crm/projects/${id}/archive`);

export const addMilestone = (projectId: string, body: Record<string, unknown>) =>
  api.post<Milestone>(`/admin/crm/projects/${projectId}/milestones`, body);
export const updateMilestone = (mid: string, body: Record<string, unknown>) =>
  api.patch<Milestone>(`/admin/crm/milestones/${mid}`, body);
export const deleteMilestone = (mid: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/milestones/${mid}`);

/**
 * Turn this project's unbilled work into an invoice.
 *
 * The body is what the invoicing wizard asks, and every field of it is optional -
 * a call with no body at all does what this endpoint did before the wizard
 * existed: one line per task, no expenses, the configured VAT rate. Hence `body`
 * itself is optional rather than filled with defaults here; the server owns those
 * defaults and a second copy would drift the day the configured rate changes.
 *
 * `taxRatePct` is a WHOLE percent. The route accepts a float and rounds it once,
 * but the invoice line-item schema is an integer, so a fractional rate produces a
 * document that cannot be saved again in the invoice editor - a refusal arriving
 * long after the click that caused it. Constrain it to integers at the input.
 *
 * The refusals: 400 `no_hourly_rate` (a project priced at nothing has no invoice
 * to write), 400 `no_billable_work` (nothing chargeable is left unbilled - it
 * covers costs as well as hours, hence not `no_billable_time` any more), and 409
 * `billed_concurrently` (somebody billed these rows between the read and the
 * write, so NOTHING was written and running it again is safe). `TimeEntries.tsx`
 * already names all of them plus the legacy `no_billable_time`.
 */
export const invoiceProjectTime = (
  id: string,
  body?: { groupBy?: "task" | "single"; includeExpenses?: boolean; taxRatePct?: number },
) =>
  api.post<{ ok: boolean; invoice: { id: string; number: string; totalCents: number } }>(
    `/admin/crm/projects/${id}/invoice-time`,
    body ?? {},
  );

// ── Tasks ───────────────────────────────────────────────────────────────
export const getTasks = (params?: Record<string, string | number | undefined>) =>
  api.get<Page<Task>>(`/admin/crm/tasks${qs(params)}`);
/** The detail carries the boxes as well as the conversation, so the card that
 *  draws them never has to make a second request and watch its own count arrive
 *  a moment after the task it belongs to. */
export const getTask = (id: string) =>
  api.get<Task & { comments: TaskComment[]; timeEntries: TimeEntry[]; checklist: ChecklistItem[] }>(
    `/admin/crm/tasks/${id}`,
  );
export const createTask = (body: Record<string, unknown>) =>
  api.post<Task>("/admin/crm/tasks", body);
export const updateTask = (id: string, body: Record<string, unknown>) =>
  api.patch<Task>(`/admin/crm/tasks/${id}`, body);
export const setTaskStatus = (id: string, status: TaskStatus) =>
  api.patch<Task>(`/admin/crm/tasks/${id}/status`, { status });
export const deleteTask = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/tasks/${id}`);
export const addTaskComment = (id: string, body: string) =>
  api.post<TaskComment>(`/admin/crm/tasks/${id}/comments`, { body });

/**
 * The tag vocabulary the task register's filter offers - `{ rows: string[] }`,
 * folded from the tasks themselves rather than kept in a table.
 *
 * The twin of `getProjectTags` above with one difference worth knowing: this one
 * is NOT narrowed by the project's archive state, because nothing in the task
 * list is either. A filter control is a promise about the rows beneath it, and
 * the tasks of an archived project are visibly on this page - so an option
 * missing here would be a filter lying about a list it can see.
 */
export const getTaskTags = () => api.get<{ rows: string[] }>("/admin/crm/task-tags");

/**
 * The checklist calls live in `taskChecklist.ts` and are re-exported here so both
 * import paths work, exactly as the portfolio rollup is above: this module is
 * where a caller already looks for anything task-shaped, and the sibling exists
 * because this file had reached the size where the next feature has to go
 * somewhere else (AGENTS.md §4).
 */
export {
  addChecklistItem, deleteChecklistItem, reorderChecklist, updateChecklistItem,
} from "./taskChecklist.js";
export type { ChecklistItem, ChecklistProgress } from "./taskChecklist.js";

// ── Time tracking ─────────────────────────────────────────────────────────────
export const getTimeEntries = (params?: Record<string, string | number | undefined>) =>
  api.get<Page<TimeEntry> & { totalMinutes: number }>(`/admin/crm/time-entries${qs(params)}`);
export const createTimeEntry = (body: Record<string, unknown>) =>
  api.post<TimeEntry>("/admin/crm/time-entries", body);
export const updateTimeEntry = (id: string, body: Record<string, unknown>) =>
  api.patch<TimeEntry>(`/admin/crm/time-entries/${id}`, body);
export const deleteTimeEntry = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/time-entries/${id}`);

// ── Contracts ────────────────────────────────────────────────────────────────
export const getContracts = (params?: Record<string, string | number | undefined>) =>
  api.get<Page<Contract>>(`/admin/crm/contracts${qs(params)}`);
export const getContract = (id: string) =>
  api.get<Contract>(`/admin/crm/contracts/${id}`);
export const createContract = (body: Record<string, unknown>) =>
  api.post<Contract>("/admin/crm/contracts", body);
export const updateContract = (id: string, body: Record<string, unknown>) =>
  api.patch<Contract>(`/admin/crm/contracts/${id}`, body);
export const signContract = (id: string) =>
  api.post<Contract>(`/admin/crm/contracts/${id}/sign`, {});
/** `emailStatus`: dev|sent|failed for the mail to the client, or skipped when
 *  the client has no e-mail address on file. */
export const sendContract = (id: string) =>
  api.post<Contract & { emailStatus?: "dev" | "sent" | "failed" | "skipped" }>(`/admin/crm/contracts/${id}/send`, {});
export const deleteContract = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/contracts/${id}`);
export const getContractTemplates = () =>
  api.get<{ templates: ContractTemplate[] }>("/admin/crm/contract-templates");
export const getContractTemplate = (key: string) =>
  api.get<ContractTemplateFull>(`/admin/crm/contract-templates/${key}`);
export const createContractTemplate = (body: ContractTemplateInput) =>
  api.post<ContractTemplateFull>("/admin/crm/contract-templates", body);
export const updateContractTemplate = (key: string, body: Partial<ContractTemplateInput>) =>
  api.patch<ContractTemplateFull>(`/admin/crm/contract-templates/${key}`, body);
export const deleteContractTemplate = (key: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/contract-templates/${key}`);

/** Fetch the contract PDF as a Blob (cookie-authenticated) and download it. */
export async function downloadContractPdf(id: string, filename: string): Promise<void> {
  const res = await fetch(`/api/admin/crm/contracts/${id}/pdf`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Client picker (shared by the project and contract modals) ──────────────────
/**
 * Clients by name, for a picker.
 *
 * `limit` exists for the callers that do NOT type: an autocomplete asks with a
 * term and twenty answers are plenty, but a toolbar filter has to offer its
 * choices before anybody has typed anything, and a menu built from twenty
 * clients is a menu that cannot select the twenty-first. 100 is the endpoint's
 * ceiling, so the honest reading of the default is "the first page of clients",
 * not "the clients".
 */
export const getClientOptions = (q?: string, limit = 20) =>
  api.get<{ rows: { id: string; name: string }[] }>(`/admin/crm/clients${qs({ q, pageSize: limit })}`);

// ── Staff (people who can own work) ───────────────────────────────────────────
/**
 * Moved to `lib/staff.ts`, which is now the only client-side description of
 * `/admin/crm/staff` - `support.ts` used to hold a second, narrower one. Kept
 * here as a re-export because the projects screens and the shared directory hook
 * import it from this path, and a rename across them is not what that fix was.
 */
export { getStaff } from "./staff.js";
export type { StaffUser } from "./staff.js";
